import { useState } from 'react';
import { api } from '../api/client';
import { ApiError, CategoryType } from '../api/types';
import { useBudget } from '../store/BudgetContext';
import { useToast } from './Toast';
import { RhythmCards } from './RhythmCards';
import { theme } from '../theme';
import { nextSortToEnd } from '../utils/category-order';

const { ink, sub, line, paper, bg } = theme.colors;

/**
 * Мини-форма создания категории.
 * После успешного создания вызывает onCreated(name) — экран откроет PlanPad.
 */
export function CategoryCreateForm(props: {
  onCancel: () => void;
  /** Вызывается после успешного create + refresh. */
  onCreated: (name: string) => void;
}): JSX.Element {
  const { onCancel, onCreated } = props;
  const { viewData, refresh } = useBudget();
  const toast = useToast();

  const [name, setName] = useState('');
  const [type, setType] = useState<CategoryType>('Расход');
  const [daily, setDaily] = useState(true);
  const [busy, setBusy] = useState(false);

  const cats = viewData?.categories ?? [];

  async function handleCreate() {
    const nm = name.trim();
    if (!nm) {
      toast.show('имя обязательно', 'error');
      return;
    }
    setBusy(true);
    try {
      await api.createCategory({
        name: nm,
        type,
        plan_month: 0,
        icon: 'tag',
        color: '#888888',
        sort_order: nextSortToEnd(cats, type),
        is_hot: false,
        is_daily: type === 'Расход' ? daily : false,
        grp: '',
      });
      await refresh();
      const повседневная = type === 'Расход' ? daily : false;
      toast.reward({
        title: `Создана «${nm}»`,
        coin: false,
        sub:
          'задай план — он сразу посчитается в ' +
          (повседневная ? 'дневной лимит' : 'конверты'),
      });
      onCreated(nm);
    } catch (e) {
      toast.show(e instanceof ApiError ? e.message : 'Ошибка', 'error');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      className="rise"
      style={{
        border: `1px solid ${ink}`,
        borderRadius: theme.radius.sm,
        padding: 14,
      }}
    >
      {/* Caption */}
      <div
        style={{
          textTransform: 'uppercase',
          letterSpacing: '.18em',
          fontSize: 11,
          color: sub,
        }}
      >
        новая категория
      </div>

      {/* Input имени */}
      <input
        autoFocus
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder="название"
        style={{
          width: '100%',
          boxSizing: 'border-box',
          marginTop: 8,
          padding: '9px 11px',
          fontSize: 16,
          border: `1px solid ${line}`,
          borderRadius: theme.radius.sm,
          background: bg,
          fontFamily: theme.font,
          color: ink,
          outline: 'none',
        }}
      />

      {/* Сегмент Расход/Доход */}
      <div style={{ display: 'flex', gap: 6, marginTop: 10 }}>
        {(['Расход', 'Доход'] as CategoryType[]).map((t) => {
          const sel = type === t;
          return (
            <button
              key={t}
              type="button"
              onClick={() => setType(t)}
              style={{
                flex: 1,
                padding: '8px 0',
                fontSize: 13,
                borderRadius: theme.radius.sm,
                border: sel ? `1px solid ${ink}` : `1px solid ${line}`,
                background: sel ? ink : 'transparent',
                color: sel ? paper : ink,
                cursor: 'pointer',
                fontFamily: theme.font,
              }}
            >
              {t}
            </button>
          );
        })}
      </div>

      {/* Подсказка типа */}
      <div
        style={{
          fontSize: 11.5,
          color: sub,
          fontStyle: 'italic',
          marginTop: 4,
        }}
      >
        тип не изменить после создания — выбери сейчас
      </div>

      {/* RhythmCards только для расходов */}
      {type === 'Расход' && (
        <div style={{ marginTop: 10 }}>
          <RhythmCards daily={daily} onChange={setDaily} perDay={null} />
        </div>
      )}

      {/* Кнопки */}
      <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
        <button
          type="button"
          onClick={handleCreate}
          disabled={busy}
          style={{
            flex: 1,
            padding: '11px 0',
            background: ink,
            color: paper,
            border: 'none',
            borderRadius: theme.radius.sm,
            fontFamily: theme.fontDisplay,
            fontSize: 15,
            cursor: busy ? 'default' : 'pointer',
          }}
        >
          {busy ? '…' : 'Создать'}
        </button>
        <button
          type="button"
          onClick={onCancel}
          style={{
            padding: '11px 16px',
            background: 'transparent',
            color: sub,
            border: `1px solid ${line}`,
            borderRadius: theme.radius.sm,
            fontSize: 13,
            cursor: 'pointer',
            fontFamily: theme.font,
          }}
        >
          Отмена
        </button>
      </div>

      {/* Подсказка про план */}
      <div
        style={{
          fontSize: 11.5,
          color: sub,
          fontStyle: 'italic',
          marginTop: 8,
        }}
      >
        после создания сразу откроется ввод плана
      </div>
    </div>
  );
}
